// src/index.ts
import { z } from "zod";
import * as E2 from "fp-ts/Either";
import { pipe as pipe2 } from "fp-ts/function";

// src/memoized-loader.ts
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/function";
var defaultErrorHandler = (error) => {
  throw new Error(formatConfigError(error));
};
var defaultSuccessHandler = () => {
};
var createLoader = (schema, options = {}) => {
  const { onError = defaultErrorHandler, onSuccess = defaultSuccessHandler } = options;
  let configInstance = null;
  return (sources) => {
    if (configInstance !== null) {
      return configInstance;
    }
    const result = loadConfigWithSources(schema, sources);
    return pipe(
      result,
      E.fold(onError, (config) => {
        onSuccess(config, sources);
        configInstance = config;
        return config;
      })
    );
  };
};
var withLogging = (appName, schema, safeFields) => ({
  onError: (error) => {
    console.error(`[${appName}] Failed to load environment configuration:`);
    console.error(formatConfigError(error));
    throw new Error(formatConfigError(error));
  },
  onSuccess: (config, sources) => {
    console.info(`[${appName}] Environment configuration loaded:`);
    console.info(
      printConfig(
        config,
        schema,
        {
          safeFields,
          showSource: true
        },
        sources
      )
    );
  }
});

// src/index.ts
var validationError = (errors) => ({
  _tag: "ValidationError",
  errors
});
var extractEnv = (env2) => E2.right(env2);
var parseWithSchema = (schema) => (env2) => {
  const result = schema.safeParse(env2);
  if (!result.success) {
    const errors = result.error.errors.map((err) => ({
      path: err.path.join("."),
      message: err.message
    }));
    return E2.left(validationError(errors));
  }
  return E2.right(result.data);
};
var enrichValidationErrors = (error) => {
  const enrichedErrors = error.errors.map((err) => {
    if (err.message === "Required" && err.path) {
      return {
        path: err.path,
        message: `Environment variable '${err.path}' is required but not found`
      };
    }
    return err;
  });
  return validationError(enrichedErrors);
};
var loadConfigWithSources = (schema, sources = [
  { name: "env", values: process.env }
]) => {
  const mergedEnv = sources.reduce(
    (acc, source) => Object.assign(acc, source.values),
    {}
  );
  return pipe2(
    extractEnv(mergedEnv),
    E2.chain(parseWithSchema(schema)),
    E2.mapLeft(enrichValidationErrors)
  );
};
var loadConfig = (schema, env2 = process.env) => pipe2(
  extractEnv(env2),
  E2.chain(parseWithSchema(schema)),
  E2.mapLeft(enrichValidationErrors)
);
var formatConfigError = (error) => {
  const errorMessages = error.errors.map((err) => `  - ${err.path}: ${err.message}`).join("\n");
  return `Configuration validation failed:
${errorMessages}`;
};
var getConfigOrThrow = (schema, env2 = process.env) => {
  const result = loadConfig(schema, env2);
  if (E2.isLeft(result)) {
    throw new Error(formatConfigError(result.left));
  }
  return result.right;
};
var getConfigOrThrowWithSources = (schema, sources = [
  { name: "env", values: process.env }
]) => {
  const result = loadConfigWithSources(schema, sources);
  if (E2.isLeft(result)) {
    throw new Error(formatConfigError(result.left));
  }
  return result.right;
};
var withPrintConfigDefaults = (config) => ({
  safeFields: [],
  formatters: {},
  showSource: true,
  ...config
});
var MIN_SECRET_LENGTH = 4;
var SHORT_SECRET_LENGTH = 8;
var MEDIUM_SECRET_LENGTH = 20;
var REDACTED_LENGTH = 8;
var REVEAL_START_SHORT = 1;
var REVEAL_END_SHORT = 1;
var REVEAL_START_MEDIUM = 2;
var REVEAL_END_MEDIUM = 2;
var REVEAL_START_LONG = 4;
var REVEAL_END_LONG = 4;
var redactSecret = (value) => {
  const len = value.length;
  if (len <= MIN_SECRET_LENGTH) {
    return "****";
  }
  if (len <= SHORT_SECRET_LENGTH) {
    return value.slice(0, REVEAL_START_SHORT) + "*".repeat(len - REVEAL_START_SHORT - REVEAL_END_SHORT) + value.slice(-REVEAL_END_SHORT);
  }
  if (len <= MEDIUM_SECRET_LENGTH) {
    return value.slice(0, REVEAL_START_MEDIUM) + "*".repeat(len - REVEAL_START_MEDIUM - REVEAL_END_MEDIUM) + value.slice(-REVEAL_END_MEDIUM);
  }
  return value.slice(0, REVEAL_START_LONG) + "*".repeat(REDACTED_LENGTH) + value.slice(-REVEAL_END_LONG);
};
var formatPrimitiveValue = (value) => {
  if (value === null || value === void 0) {
    return "null";
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return String(value);
};
var formatValue = (value, isSecret) => {
  if (typeof value === "string") {
    return isSecret ? redactSecret(value) : value;
  }
  if (Array.isArray(value)) {
    return isSecret ? "[redacted array]" : JSON.stringify(value);
  }
  if (typeof value === "object" && value !== null) {
    return isSecret ? "[redacted object]" : JSON.stringify(value);
  }
  return formatPrimitiveValue(value);
};
var getValueSource = (key, _schema, sources) => {
  const envKey = String(key);
  for (const source of [...sources].reverse()) {
    if (source?.values?.[envKey] !== void 0) {
      return source.name;
    }
  }
  return "default";
};
var printConfig = (config, schema, options = {}, sources = [
  { name: "env", values: process.env }
]) => {
  const { safeFields, formatters, showSource } = withPrintConfigDefaults(options);
  const lines = ["Configuration loaded:"];
  const maxKeyLength = Math.max(...Object.keys(config).map((k) => k.length));
  for (const [key, value] of Object.entries(config)) {
    const typedKey = key;
    const isSafe = safeFields.includes(typedKey);
    const formatter = formatters?.[typedKey];
    let displayValue;
    if (formatter) {
      displayValue = formatter(value);
    } else {
      displayValue = formatValue(value, !isSafe);
    }
    const paddedKey = key.padEnd(maxKeyLength);
    let line = `  ${paddedKey} : ${displayValue}`;
    if (showSource) {
      const source = getValueSource(typedKey, schema, sources);
      const sourceTag = `[${source}]`;
      line += ` ${sourceTag}`;
    }
    lines.push(line);
  }
  return lines.join("\n");
};
var hostname = () => z.string().regex(
  /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9-_]+\.[a-zA-Z]{2,}$/,
  "Hostname must be a valid domain name"
);
var port = () => z.string().transform(Number).pipe(z.number().int().min(1).max(65535));
var url = (protocols) => {
  if (protocols) {
    return z.string().refine((val) => protocols.some((p) => val.startsWith(`${p}://`)), {
      message: `URL must start with one of: ${protocols.join(", ")}`
    });
  }
  return z.string().url();
};
var bool = () => z.string().transform((val) => val.toLowerCase()).transform(
  (val) => val === "true" || val === "1" || val === "yes" || val === "on"
);
var json = () => z.string().transform((val, ctx) => {
  try {
    return JSON.parse(val);
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Invalid JSON"
    });
    return z.NEVER;
  }
});
var list = (delimiter = ",") => z.string().transform(
  (val) => val.split(delimiter).map((item) => item.trim()).filter(Boolean)
);
var int = () => z.string().transform(Number).pipe(z.number().int());
var float = () => z.string().transform(Number).pipe(z.number());
var email = () => z.string().email();
var duration = () => z.string().regex(/^\d+[smhd]$/, "Duration must be in format: 30s, 5m, 2h, 1d");
var bytes = () => z.string().regex(/^\d+[KMGT]?B?$/i, "Bytes must be in format: 1024, 1KB, 5MB, 2GB");
var percent = () => z.string().transform((val) => val.replace("%", "")).transform(Number).pipe(z.number().min(0).max(100));
var nodeEnv = () => z.enum(["development", "production", "test", "staging"]);
var logLevel = () => z.enum(["error", "warn", "info", "debug", "trace"]);
var env = {
  hostname,
  port,
  url,
  bool,
  json,
  list,
  int,
  float,
  email,
  duration,
  bytes,
  percent,
  nodeEnv,
  logLevel
};
export {
  createLoader,
  env,
  extractEnv,
  formatConfigError,
  getConfigOrThrow,
  getConfigOrThrowWithSources,
  loadConfig,
  loadConfigWithSources,
  parseWithSchema,
  printConfig,
  withLogging
};
